import { z } from 'zod';
import {
  CRITERIA_BY_KEY,
  SCORE_DIMENSION_LABELS,
  SCORE_DIMENSIONS,
  ScoreDimensionSchema,
  type CriterionKey,
  type CriterionResultValue,
  type EvaluationPolicy,
  type ScoreDimension,
  type Severity,
} from './evaluation.js';

// Six explained advisor scores (spec §22). Computed IN CODE from persisted criterion results:
// 1) per criterion: points = passed 1 · partial 0.5 · failed 0, each row weighted by severity (failures that
//    matter more count more: low 0.75 · medium 1 · high 1.5 · critical 2); pending/unknown/not_applicable rows
//    are excluded (never a 0);
// 2) per dimension: registry-weighted mean of its criteria that have scored rows (weight-0 = informational);
// 3) overall: tenant score weights over the dimensions that have a score.
// Every score comes with its "why": the criteria that lost the most points, with example conversations.

export const SCORE_VERSION = 'advisor-score-v1';

const POINTS: Partial<Record<CriterionResultValue, number>> = { passed: 1, partial: 0.5, failed: 0 };
const SEVERITY_WEIGHT: Record<Severity, number> = { low: 0.75, medium: 1, high: 1.5, critical: 2 };

export interface ScoreRow {
  criterionKey: string;
  result: CriterionResultValue;
  severity: Severity | null;
  conversationId?: string;
  episodeId?: string;
  explanation?: string | null;
  evidenceMessageIds?: readonly string[];
}

export const CriterionContributionSchema = z.object({
  criterionKey: z.string(),
  label: z.string(),
  weight: z.number(),
  /** 0–100 for this criterion; null when nothing was scored. */
  score: z.number().nullable(),
  passed: z.number().int(),
  partial: z.number().int(),
  failed: z.number().int(),
  /** Points of the dimension score lost because of this criterion (0–100 scale). */
  lostPoints: z.number(),
  examples: z.array(z.object({ conversationId: z.string(), episodeId: z.string(), explanation: z.string().nullable(), evidenceMessageIds: z.array(z.string()) })),
});
export type CriterionContribution = z.infer<typeof CriterionContributionSchema>;

export const DimensionScoreSchema = z.object({
  dimension: ScoreDimensionSchema,
  label: z.string(),
  /** Normalized share of the overall score (0–1) among available dimensions; null when not scored. */
  share: z.number().nullable(),
  score: z.number().nullable(),
  /** Scored rows (passed + partial + failed). */
  observations: z.number().int(),
  /** Rows not scored (pending / unknown / not applicable). */
  excluded: z.number().int(),
  /** Criteria sorted by lost points (largest first): the explanation of the score. */
  contributions: z.array(CriterionContributionSchema),
});
export type DimensionScore = z.infer<typeof DimensionScoreSchema>;

export const ScoreCardSchema = z.object({
  version: z.string(),
  overall: z.number().nullable(),
  dimensions: z.array(DimensionScoreSchema),
});
export type ScoreCard = z.infer<typeof ScoreCardSchema>;

const MAX_EXAMPLES = 3;

export function computeScoreCard(rows: readonly ScoreRow[], weights: EvaluationPolicy['scoreWeights']): ScoreCard {
  const byCriterion = new Map<string, ScoreRow[]>();
  for (const r of rows) {
    const def = CRITERIA_BY_KEY[r.criterionKey as CriterionKey];
    if (!def) continue;
    const list = byCriterion.get(r.criterionKey) ?? [];
    list.push(r);
    byCriterion.set(r.criterionKey, list);
  }

  const dims = SCORE_DIMENSIONS.map((dimension) => {
    const contributions: CriterionContribution[] = [];
    let observations = 0;
    let excluded = 0;
    for (const [key, list] of byCriterion) {
      const def = CRITERIA_BY_KEY[key as CriterionKey]!;
      if (def.dimension !== dimension || def.weight <= 0) continue;
      let num = 0;
      let den = 0;
      let passed = 0;
      let partial = 0;
      let failed = 0;
      for (const r of list) {
        const p = POINTS[r.result];
        if (p === undefined) {
          excluded++;
          continue;
        }
        const w = r.result === 'passed' ? 1 : SEVERITY_WEIGHT[r.severity ?? 'medium'];
        num += w * p;
        den += w;
        if (r.result === 'passed') passed++;
        else if (r.result === 'partial') partial++;
        else failed++;
      }
      observations += passed + partial + failed;
      const examples = list
        .filter((r) => (r.result === 'failed' || r.result === 'partial') && r.conversationId && r.episodeId)
        .sort((a, b) => SEVERITY_WEIGHT[b.severity ?? 'medium'] - SEVERITY_WEIGHT[a.severity ?? 'medium'] || (a.result === 'failed' ? -1 : 1))
        .slice(0, MAX_EXAMPLES)
        .map((r) => ({ conversationId: r.conversationId!, episodeId: r.episodeId!, explanation: r.explanation ?? null, evidenceMessageIds: [...(r.evidenceMessageIds ?? [])].slice(0, 5) }));
      contributions.push({ criterionKey: key, label: def.label, weight: def.weight, score: den > 0 ? (100 * num) / den : null, passed, partial, failed, lostPoints: 0, examples });
    }
    const scored = contributions.filter((c) => c.score !== null);
    const totalWeight = scored.reduce((s, c) => s + c.weight, 0);
    for (const c of scored) c.lostPoints = totalWeight > 0 ? (c.weight * (100 - c.score!)) / totalWeight : 0;
    contributions.sort((a, b) => b.lostPoints - a.lostPoints || a.label.localeCompare(b.label));
    const score = totalWeight > 0 ? scored.reduce((s, c) => s + c.weight * c.score!, 0) / totalWeight : null;
    return { dimension, label: SCORE_DIMENSION_LABELS[dimension], share: null as number | null, score, observations, excluded, contributions };
  });

  const available = dims.filter((d) => d.score !== null && weights[d.dimension] > 0);
  const totalWeight = available.reduce((s, d) => s + weights[d.dimension], 0);
  for (const d of available) d.share = totalWeight > 0 ? weights[d.dimension] / totalWeight : null;
  const overall = totalWeight > 0 ? available.reduce((s, d) => s + weights[d.dimension] * d.score!, 0) / totalWeight : null;
  const round = (v: number | null) => (v === null ? null : Math.round(v * 10) / 10);
  return {
    version: SCORE_VERSION,
    overall: round(overall),
    dimensions: dims.map((d) => ({ ...d, score: round(d.score), contributions: d.contributions.map((c) => ({ ...c, score: round(c.score), lostPoints: round(c.lostPoints) ?? 0 })) })),
  };
}

export function dimensionScore(card: ScoreCard, d: ScoreDimension): number | null {
  return card.dimensions.find((x) => x.dimension === d)?.score ?? null;
}

// ───────── Calibration (§22: ≥ 20–30 manually reviewed conversations before comparing people) ─────────

export const CALIBRATION_TARGET = 20;

export const CalibrationReviewInputSchema = z.object({
  /** Reviewer's own 0–100 score per dimension (null = could not judge). */
  scores: z.object(Object.fromEntries(SCORE_DIMENSIONS.map((d) => [d, z.number().min(0).max(100).nullable()])) as Record<ScoreDimension, z.ZodNullable<z.ZodNumber>>),
  /** Free-text calibration note (what the AI got right/wrong). */
  notes: z.string().trim().max(2000).nullable(),
});
export type CalibrationReviewInput = z.infer<typeof CalibrationReviewInputSchema>;

export const CalibrationReviewSchema = CalibrationReviewInputSchema.extend({
  id: z.string(),
  conversationId: z.string(),
  episodeId: z.string(),
  reviewerName: z.string(),
  /** Scores the system had when the review was saved (for agreement tracking). */
  systemScores: z.object(Object.fromEntries(SCORE_DIMENSIONS.map((d) => [d, z.number().nullable()])) as Record<ScoreDimension, z.ZodNullable<z.ZodNumber>>),
  scoreVersion: z.string(),
  updatedAt: z.string(),
});
export type CalibrationReview = z.infer<typeof CalibrationReviewSchema>;

export const CalibrationSummarySchema = z.object({
  target: z.number().int(),
  reviewed: z.number().int(),
  ready: z.boolean(),
  /** Mean absolute difference reviewer vs system per dimension (null without paired observations). */
  meanAbsoluteDifference: z.object(Object.fromEntries(SCORE_DIMENSIONS.map((d) => [d, z.number().nullable()])) as Record<ScoreDimension, z.ZodNullable<z.ZodNumber>>),
  recent: z.array(CalibrationReviewSchema.extend({ contactName: z.string() })),
});
export type CalibrationSummary = z.infer<typeof CalibrationSummarySchema>;
